import {
  Grid,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import FolderIcon from '@material-ui/icons/Folder';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { matlabVncSessionApiRef, PersistedSessionState } from '../matlab/matlabVncSessionApi';
import { MatlabVncSessionCard } from '../matlab/MatlabVncSessionCard';
import { Page, Header, Content } from '@backstage/core-components';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

const FONT = "'Arial', sans-serif";
const MATLAB_SESSION_BOX_STORAGE_KEY = 'catalog-home.matlab-session-box';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    background: theme.palette.background.paper,
    borderRadius: '4px',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
    height: '100%',
    boxSizing: 'border-box' as const,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    padding: '16px',
    gap: '8px',
    width: '100%',
    boxSizing: 'border-box' as const,
    flexGrow: 1,
  },
  cardLabel: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.text.secondary,
    marginBottom: '4px',
  },
  cardHeadline: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '28px',
    color: theme.palette.text.primary,
    margin: '0 0 8px 0',
  },
  cardBody: {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.text.secondary,
    flexGrow: 1,
    margin: '0 0 16px 0',
  },
  cardLink: {
    display: 'inline-flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: '4px',
    fontFamily: FONT,
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.primary.main,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  sectionLabel: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '20px',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
}));

type CatalogCardProps = {
  label?: string;
  title: string;
  description: string;
  linkLabel: string;
  to?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
};

const CatalogCard = ({ label, title, description, linkLabel, to, onClick, style }: CatalogCardProps) => {
  const classes = useStyles();
  return (
    <div className={classes.card} style={style}>
      <div className={classes.cardContent}>
        {label && <span className={classes.cardLabel}>{label}</span>}
        <h2 className={classes.cardHeadline}>{title}</h2>
        <p className={classes.cardBody}>{description}</p>
        {to ? (
          <RouterLink to={to} className={classes.cardLink}>
            {linkLabel}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </RouterLink>
        ) : (
          <button className={classes.cardLink} onClick={onClick} type="button">
            {linkLabel}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};



export const CatalogHomePage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const sessionApi = useApi(matlabVncSessionApiRef);

  // Track all minimized sessions from global API
  const [sessions, setSessions] = useState<PersistedSessionState[]>(
    () => sessionApi.getAllSessions(),
  );

  // ── Matlab / VNC workspace dialog state ─────────────────────────────────
  type DialogState = 'closed' | 'idle' | 'configure-token' | 'choose-folder' | 'pick-existing' | 'new-name' | 'launching' | 'active' | 'busy';
  const [dialogState, setDialogState] = useState<DialogState>('closed');
  const [licenseStatus, setLicenseStatus] = useState<{
    active: number;
    total: number;
    available: number;
  } | null>(null);
  const [vncUrl, setVncUrl] = useState<string | null>(null);
  const [sessionMinimized, setSessionMinimized] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [sessionBoxHydrated, setSessionBoxHydrated] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [conflictMsg, setConflictMsg] = useState<string | null>(null);

  // ── Token state ───────────────────────────────────────────────────────────
  const [tokenStatus, setTokenStatus] = useState<{ configured: boolean; is_expired?: boolean; days_remaining?: number } | null>(null);
  const [mlflowToken, setMlflowToken] = useState('');
  const [tokenSaving, setTokenSaving] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Workspace folder state
  const [newFolderName, setNewFolderName] = useState('');
  const [activeFolderName, setActiveFolderName] = useState('');
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<number | null>(null);

  // Available folders for the launch picker (own + others' public)
  interface DiskFolder {
    id: number | null;
    owner_username: string;
    folder_name: string;
    folder_path: string;
    is_public: boolean;
    last_saved_at: string | null;
    created_at: string | null;
    active_session_id: string | null;
    on_disk: boolean;
    is_mine: boolean;
  }
  const [diskFolders, setDiskFolders] = useState<DiskFolder[]>([]);
  const [loadingDisk, setLoadingDisk] = useState(false);
  const [pickedFolder, setPickedFolder] = useState<DiskFolder | null>(null);

  // Intervals
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Subscribe to session state changes from global API
  useEffect(() => {
    return sessionApi.subscribe((allSessions: PersistedSessionState[]) => {
      setSessions(allSessions);
      const firstSession = allSessions[0] ?? null;
      if (firstSession) {
        // Sync first session data to local state when restored from global API
        setActiveFolderName(firstSession.folderName);
        setActiveWorkspaceId(firstSession.workspaceId);
        setActiveSessionId(firstSession.sessionId);
        setSessionMinimized(true);
      } else {
        // Clear all session data if no sessions
        setActiveFolderName('');
        setActiveWorkspaceId(null);
        setActiveSessionId(null);
        setSessionMinimized(false);
      }
    });
  }, [sessionApi]);

  // Persist minimized/active session box so it survives page navigation.
  useEffect(() => {
    if (!sessionBoxHydrated) {
      return;
    }
    try {
      const shouldPersist =
        !!vncUrl &&
        !!activeFolderName &&
        sessionMinimized;
      if (!shouldPersist) {
        sessionStorage.removeItem(MATLAB_SESSION_BOX_STORAGE_KEY);
        return;
      }
      sessionStorage.setItem(
        MATLAB_SESSION_BOX_STORAGE_KEY,
        JSON.stringify({
          vncUrl,
          activeFolderName,
          activeWorkspaceId,
          activeSessionId,
        }),
      );
    } catch {
      // non-fatal
    }
  }, [
    sessionBoxHydrated,
    sessionMinimized,
    vncUrl,
    activeFolderName,
    activeWorkspaceId,
    activeSessionId,
  ]);

  // Restore persisted session box only if backend still reports active session.
  useEffect(() => {
    let cancelled = false;

    const restoreSessionBox = async () => {
      let parsed: {
        vncUrl: string;
        activeFolderName: string;
        activeWorkspaceId: number | null;
        activeSessionId: string | null;
      } | null = null;
      try {
        const raw = sessionStorage.getItem(MATLAB_SESSION_BOX_STORAGE_KEY);
        if (!raw) return;
        const candidate = JSON.parse(raw) as {
          vncUrl?: unknown;
          activeFolderName?: unknown;
          activeWorkspaceId?: unknown;
          activeSessionId?: unknown;
        };
        if (
          typeof candidate.vncUrl !== 'string' ||
          typeof candidate.activeFolderName !== 'string'
        ) {
          sessionStorage.removeItem(MATLAB_SESSION_BOX_STORAGE_KEY);
          return;
        }
        parsed = {
          vncUrl: candidate.vncUrl,
          activeFolderName: candidate.activeFolderName,
          activeWorkspaceId:
            typeof candidate.activeWorkspaceId === 'number'
              ? candidate.activeWorkspaceId
              : null,
          activeSessionId:
            typeof candidate.activeSessionId === 'string'
              ? candidate.activeSessionId
              : null,
        };
      } catch {
        sessionStorage.removeItem(MATLAB_SESSION_BOX_STORAGE_KEY);
        return;
      }

      try {
        const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
        const { token } = await identityApi.getCredentials();
        const resp = await fetch(`${baseUrl}/workspaces/available-folders`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await resp.json();
        const folders: any[] = data.folders ?? [];

        const stillActive = folders.some(f => {
          const idMatches =
            parsed.activeWorkspaceId !== null &&
            Number(f.id) === Number(parsed.activeWorkspaceId);
          const folderMatches = f.folder_name === parsed.activeFolderName;
          return !!f.active_session_id && (idMatches || folderMatches);
        });

        if (!stillActive) {
          // Keep persisted state; transient mismatches on navigation should
          // not hide the minimized session box unexpectedly.
          if (cancelled) return;
          setVncUrl(parsed.vncUrl);
          setActiveFolderName(parsed.activeFolderName);
          setActiveWorkspaceId(parsed.activeWorkspaceId);
          setActiveSessionId(parsed.activeSessionId);
          setSessionMinimized(true);
          return;
        }

        if (cancelled) return;
        setVncUrl(parsed.vncUrl);
        setActiveFolderName(parsed.activeFolderName);
        setActiveWorkspaceId(parsed.activeWorkspaceId);
        setActiveSessionId(parsed.activeSessionId);
        setSessionMinimized(true);
      } catch {
        // If backend check fails transiently, keep persisted state so user can reopen.
        if (cancelled || !parsed) return;
        setVncUrl(parsed.vncUrl);
        setActiveFolderName(parsed.activeFolderName);
        setActiveWorkspaceId(parsed.activeWorkspaceId);
        setActiveSessionId(parsed.activeSessionId);
        setSessionMinimized(true);
      } finally {
        if (!cancelled) {
          setSessionBoxHydrated(true);
        }
      }
    };

    restoreSessionBox();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, identityApi]);

  // Fetch /status and update licenseStatus
  const fetchStatus = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        setLicenseStatus(data);
        return data as { active: number; total: number; available: number };
      }
    } catch {
      // ignore
    }
    return null;
  }, [discoveryApi, identityApi]);

  // Start heartbeat pings every 30 s
  const startHeartbeat = useCallback(
    (sid: string) => {
      stopHeartbeat();
      heartbeatRef.current = setInterval(async () => {
        try {
          const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
          const { token } = await identityApi.getCredentials();
          await fetch(`${baseUrl}/heartbeat/${sid}`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
        } catch { /* non-fatal */ }
      }, 30_000);
    },
    [discoveryApi, identityApi, stopHeartbeat],
  );

  // Fetch available folders for the launch picker (own folders + others' public)
  const fetchDiskFolders = useCallback(async () => {
    setLoadingDisk(true);
    setPickedFolder(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/workspaces/available-folders`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await resp.json();
      setDiskFolders(data.folders ?? []);
    } catch {
      setDiskFolders([]);
    } finally {
      setLoadingDisk(false);
    }
  }, [discoveryApi, identityApi]);

  // Launch with a specific folder_name
  const doWorkspaceLaunch = useCallback(async (folderName: string) => {
    setDialogState('launching');
    setErrorMsg(null);
    setConflictMsg(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();

      const resp = await fetch(`${baseUrl}/workspaces/launch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ folder_name: folderName }),
      });

      if (resp.status === 423) {
        const data = await resp.json();
        setLicenseStatus({ active: data.active, total: data.total, available: 0 });
        setDialogState('busy');
        stopPoll();
        pollRef.current = setInterval(async () => {
          const status = await fetchStatus();
          if (status && status.available > 0) {
            stopPoll();
            doWorkspaceLaunch(folderName);
          }
        }, 15_000);
        return;
      }

      if (resp.status === 409) {
        const data = await resp.json();
        setConflictMsg(data.message ?? 'Folder is locked.');
        setDialogState('choose-folder');
        return;
      }

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setErrorMsg(data.error ?? 'Failed to start session');
        setDialogState('choose-folder');
        return;
      }

      const data = await resp.json();
      setActiveFolderName(data.folderName ?? folderName);
      setActiveWorkspaceId(data.workspaceId ?? null);

      // Grant the VNC session cookie
      await fetch(`${baseUrl}/grant-vnc-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ slot: data.slot }),
        credentials: 'include',
      });

      const resolvedUrl = data.vncPath
        ? `${window.location.origin}${data.vncPath}`
        : data.vncUrl;
      setVncUrl(resolvedUrl);
      setSessionMinimized(false);
      setActiveSessionId(data.sessionId ?? null);
      startHeartbeat(data.sessionId);
      window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
      // Add to global API and close dialog (session will appear in floating card)
      if (data.sessionId) {
        sessionApi.addSession({
          sessionId: data.sessionId,
          workspaceId: data.workspaceId ?? 0,
          folderName: data.folderName ?? folderName,
          vncPath: data.vncPath ?? null,
          slotNum: data.slot ?? null,
          isOwnerOfActive: true,
          isPublic: false,
        });
        sessionApi.startHeartbeat(
          window.location.origin + '/api/matlab-vnc',
          () => identityApi.getCredentials().then(c => c.token ?? undefined),
        );
      }
      setSessionMinimized(true);
      setDialogState('closed');
      fetchStatus();
    } catch (e: any) {
      setErrorMsg(e?.message ?? 'Network error');
      setDialogState('choose-folder');
    }
  }, [discoveryApi, identityApi, fetchStatus, startHeartbeat, stopPoll, sessionApi]);

  // Launch an existing workspace by ID (used for public/shared folders)
  const doLaunchExistingById = useCallback(async (wsId: number, folderName: string) => {
    setDialogState('launching');
    setErrorMsg(null);
    setConflictMsg(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/workspaces/launch-existing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ workspace_id: wsId }),
      });
      if (resp.status === 423) {
        const data = await resp.json();
        setLicenseStatus({ active: data.active, total: data.total, available: 0 });
        setDialogState('busy');
        stopPoll();
        pollRef.current = setInterval(async () => {
          const status = await fetchStatus();
          if (status && status.available > 0) {
            stopPoll();
            doLaunchExistingById(wsId, folderName);
          }
        }, 15_000);
        return;
      }
      if (resp.status === 409) {
        const data = await resp.json();
        setConflictMsg(data.message ?? 'Folder is locked.');
        setDialogState('choose-folder');
        return;
      }
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setErrorMsg(data.error ?? 'Failed to start session');
        setDialogState('choose-folder');
        return;
      }
      const data = await resp.json();
      setActiveFolderName(data.folderName ?? folderName);
      setActiveWorkspaceId(data.workspaceId ?? wsId);
      
      // Find folder in diskFolders to get is_public and is_mine flags
      const folder = diskFolders.find(f => Number(f.id) === wsId);
      
      await fetch(`${baseUrl}/grant-vnc-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ slot: data.slot }),
        credentials: 'include',
      });
      const resolvedUrl = data.vncPath
        ? `${window.location.origin}${data.vncPath}`
        : data.vncUrl;
      setVncUrl(resolvedUrl);
      setSessionMinimized(false);
      setActiveSessionId(data.sessionId ?? null);
      startHeartbeat(data.sessionId);
      window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
      // Add to global API and close dialog (session will appear in floating card)
      if (data.sessionId) {
        sessionApi.addSession({
          sessionId: data.sessionId,
          workspaceId: wsId,
          folderName: data.folderName ?? folderName,
          vncPath: data.vncPath ?? null,
          slotNum: data.slot ?? null,
          isOwnerOfActive: folder?.is_mine ?? false,
          isPublic: folder?.is_public ?? false,
        });
        sessionApi.startHeartbeat(
          window.location.origin + '/api/matlab-vnc',
          () => identityApi.getCredentials().then(c => c.token ?? undefined),
        );
      }
      setSessionMinimized(true);
      setDialogState('closed');
      fetchStatus();
    } catch (e: any) {
      setErrorMsg(e?.message ?? 'Network error');
      setDialogState('choose-folder');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveryApi, identityApi, fetchStatus, startHeartbeat, stopPoll, sessionApi, diskFolders]);

  // Save session log + kill container
  // Fetch token status from backend
  const fetchTokenStatus = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/user-mlflow-token`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        setTokenStatus(data);
      }
    } catch {
      // ignore
    }
  }, [discoveryApi, identityApi]);

  const handleSaveToken = useCallback(async () => {
    if (!mlflowToken.trim()) { setTokenError('Token cannot be empty'); return; }
    setTokenSaving(true);
    setTokenError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/user-mlflow-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ token: mlflowToken }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setTokenError(data.error ?? 'Failed to save token');
        return;
      }
      setMlflowToken('');
      await fetchTokenStatus();
      setDialogState('idle');
    } catch (e: any) {
      setTokenError(e?.message ?? 'Network error');
    } finally {
      setTokenSaving(false);
    }
  }, [mlflowToken, discoveryApi, identityApi, fetchTokenStatus]);

  const handleDeleteToken = useCallback(async () => {
    setTokenSaving(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      await fetch(`${baseUrl}/user-mlflow-token`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setTokenStatus({ configured: false });
    } catch { /* ignore */ } finally {
      setTokenSaving(false);
    }
  }, [discoveryApi, identityApi]);

  const openDialog = useCallback(async () => {
    // Always show the launch options (idle state)
    // Session management (end/minimize) happens via floating card only
    setErrorMsg(null);
    setConflictMsg(null);
    setDialogState('idle');
    fetchStatus();
    fetchTokenStatus();
  }, [fetchStatus, fetchTokenStatus]);

  const openWithInfraSession = useCallback(async (url: string) => {
    try {
      const { token } = await identityApi.getCredentials();
      const resp = await fetch('/api/infra/grant-session', {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // best-effort; if grant fails the tab will simply get a 401 from nginx
    }
  }, [identityApi]);

  const closeDialog = useCallback(() => {
    stopPoll();
    stopHeartbeat();
    setVncUrl(null);
    setActiveSessionId(null);
    setSessionMinimized(false);
    setDialogState('closed');
  }, [
    stopHeartbeat,
    stopPoll,
  ]);

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      stopHeartbeat();
      stopPoll();
    };
  }, [stopHeartbeat, stopPoll]);

  return (
    <>
      <Page themeId="home">
      <Header title="CODE4EV Catalog" />
      <Content>

        <Grid container spacing={3}>
          {/* Tools */}
          <Grid item xs={12}>
            <p className={classes.sectionLabel}>Tools</p>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Repository"
                  title="Artifact Repository"
                  description="Browse, search and track all catalog entities and open issues."
                  linkLabel="Open Repository"
                  to="/artifacts"
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="IDE"
                  title="co-IDE"
                  description="Launch Web-based IDE with a selected repository."
                  linkLabel="Launch"
                  to="/co-ide"
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Storage"
                  title="Persistence Repository"
                  description="Store and manage large artifacts (ML models, firmware, datasets, documents) outside Git version control."
                  linkLabel="Open"
                  to="/persistence"
                  style={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Engineering */}
          <Grid item xs={12}>
            <p className={classes.sectionLabel}>Engineering</p>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Systems Engineering"
                  title="MBSE"
                  description="Model-Based Systems Engineering tools and model repositories."
                  linkLabel="Open MBSE"
                  onClick={() => window.open('/mbse/', '_blank', 'noopener')} 
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Simulation"
                  title="Model Composer"
                  description="Launch a MATLAB VNC desktop session or run a task."
                  linkLabel="Open"
                  onClick={openDialog}
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Machine Learning"
                  title="AI Trainer"
                  description="Launch training pipelines. Powered by MLflow."
                  linkLabel="Open AI Trainer"
                  to="/ai-trainer"
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Trust & Compliance"
                  title="AITrustSDVOps"
                  description="Assess and monitor AI trustworthiness for software-defined vehicle operations."
                  linkLabel="Open AITrustSDVOps"
                  onClick={() => openWithInfraSession('/ai-trust/')}
                  style={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Infrastructure */}
          <Grid item xs={12}>
            <p className={classes.sectionLabel}>Infrastructure</p>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Monitoring"
                  title="Infrastructure Logger"
                  description="Browse and search container logs from all running services in real-time."
                  linkLabel="Open Logger"
                  onClick={() => openWithInfraSession('/infra-logger/')}
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Monitoring"
                  title="Infrastructure Monitor"
                  description="View live infrastructure metrics via Netdata."
                  linkLabel="Open Monitor"
                  onClick={() => openWithInfraSession('/infra-monitoring/')}
                  style={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Use Cases */}
          <Grid item xs={12}>
            <p className={classes.sectionLabel}>Use Case Based Development</p>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Use Case 1"
                  title="Data-Driven EV Optimisation"
                  description="Use Case 1 Development cards"
                  linkLabel="Open"
                  to="/catalog/use-cases/1"
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Use Case 2"
                  title="EV Health Monitoring & Predictive Maintenance"
                  description="Use Case 2 Development cards"
                  linkLabel="Open"
                  to="/catalog/use-cases/2"
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <CatalogCard
                  label="Use Case 3"
                  title="Smart Motion Control"
                  description="Use Case 3 Development cards"
                  linkLabel="Open"
                  to="/catalog/use-cases/3"
                  style={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Dialog
          open={dialogState !== 'closed'}
          onClose={['launching'].includes(dialogState) ? undefined : closeDialog}
          aria-labelledby="matlab-dialog-title"
          maxWidth={['pick-existing'].includes(dialogState) ? 'lg' : ['choose-folder', 'new-name', 'configure-token'].includes(dialogState) ? 'md' : 'sm'}
          fullWidth
        >
          {/* ── idle: choose Launch MATLAB session or Run a task ── */}
          {dialogState === 'idle' && (
            <>
              <DialogTitle id="matlab-dialog-title">Model Composer</DialogTitle>
              <DialogContent>
                {licenseStatus && (
                  <Typography variant="caption" style={{ display: 'block', marginBottom: 8, fontWeight: 600,
                    color: (licenseStatus.available - sessions.length) <= 0 ? theme.palette.error.main : theme.palette.success.main }}>
                    Licenses: {Math.max(0, licenseStatus.available - sessions.length)}/{licenseStatus.total} available
                  </Typography>
                )}
                {/* MLflow token status */}
                <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 4,
                  border: `1px solid ${theme.palette.divider}`,
                  background: theme.palette.background.default }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <Typography variant="caption" style={{ fontWeight: 700, display: 'block' }}>
                        MLflow Token (AITrainer)
                      </Typography>
                      {!tokenStatus?.configured && (
                        <Typography variant="caption" color="textSecondary">Not configured — MLflow logging will not be available</Typography>
                      )}
                      {tokenStatus?.configured && !tokenStatus.is_expired && (
                        <Typography variant="caption" style={{ color: theme.palette.success.main }}>
                          ✓ Active — {tokenStatus.days_remaining} day{tokenStatus.days_remaining !== 1 ? 's' : ''} remaining
                        </Typography>
                      )}
                      {tokenStatus?.configured && tokenStatus.is_expired && (
                        <Typography variant="caption" style={{ color: theme.palette.error.main }}>
                          ✗ Expired — please reconfigure
                        </Typography>
                      )}
                    </div>
                    <Button size="small" variant="outlined"
                      onClick={() => { setTokenError(null); setMlflowToken(''); setDialogState('configure-token'); }}
                    >
                      {tokenStatus?.configured ? 'Update' : 'Configure'}
                    </Button>
                  </div>
                </div>
                <Typography variant="body2" color="textSecondary">
                  Launch MATLAB through VNC desktop or submit a script task.
                </Typography>
                {errorMsg && (
                  <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>
                    {errorMsg}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setConflictMsg(null);
                    setErrorMsg(null);
                    setNewFolderName('');
                    setDialogState('choose-folder');
                  }}
                  color="primary"
                  variant="contained"
                  disabled={!licenseStatus || (licenseStatus.available - sessions.length) <= 0}
                >
                  Launch VNC
                </Button>
                <Button onClick={() => { closeDialog(); navigate('/matlab'); }} color="primary">
                  Run a task
                </Button>
              </DialogActions>
            </>
          )}

          {/* ── configure-token ── */}
          {dialogState === 'configure-token' && (
            <>
              <DialogTitle id="matlab-dialog-title">Configure MLflow Token</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                  Generate a token in <strong>AITrainer</strong> (User Settings → Access Tokens), paste it here.
                  It will be stored in the backend and used automatically when you launch VNC or run a task.
                  Expiry is synced from the AITrainer token metadata.
                </Typography>
                <TextField
                  label="MLflow Token"
                  type="password"
                  variant="outlined"
                  fullWidth
                  autoFocus
                  value={mlflowToken}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setMlflowToken(e.target.value); setTokenError(null); }}
                  placeholder="Paste your MLflow token here"
                  disabled={tokenSaving}
                  style={{ marginBottom: 12 }}
                />
                {tokenError && (
                  <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 4 }}>
                    {tokenError}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                {tokenStatus?.configured && (
                  <Button onClick={handleDeleteToken} color="secondary" disabled={tokenSaving} style={{ marginRight: 'auto' }}>
                    Remove Token
                  </Button>
                )}
                <Button onClick={() => setDialogState('idle')} disabled={tokenSaving}>Back</Button>
                <Button onClick={handleSaveToken} color="primary" variant="contained"
                  disabled={!mlflowToken.trim() || tokenSaving}>
                  {tokenSaving ? <CircularProgress size={18} /> : 'Save Token'}
                </Button>
              </DialogActions>
            </>
          )}

          {/* ── choose-folder: create new OR use existing ── */}
          {dialogState === 'choose-folder' && (
            <>
              <DialogTitle id="matlab-dialog-title">Launch MATLAB Session</DialogTitle>
              <DialogContent>
                {conflictMsg && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                    background: theme.palette.error.main, color: theme.palette.error.contrastText, borderRadius: 4, marginBottom: 16 }}>
                    <ErrorOutlineIcon fontSize="small" />
                    <Typography variant="body2">{conflictMsg}</Typography>
                  </div>
                )}
                {errorMsg && (
                  <Typography variant="caption" color="error" style={{ display: 'block', marginBottom: 12 }}>
                    {errorMsg}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
                  How would you like to start your MATLAB session?
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Button variant="contained" color="primary" size="large" fullWidth
                    style={{ justifyContent: 'flex-start', padding: '14px 20px', textAlign: 'left' }}
                    onClick={() => { setErrorMsg(null); setNewFolderName(''); setDialogState('new-name'); }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>A) Create New Folder</div>
                      <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 400, marginTop: 2 }}>
                        Name a new folder and launch MATLAB with it as your working directory
                      </div>
                    </div>
                  </Button>
                  <Button variant="outlined" color="primary" size="large" fullWidth
                    style={{ justifyContent: 'flex-start', padding: '14px 20px', textAlign: 'left' }}
                    onClick={() => { setErrorMsg(null); fetchDiskFolders(); setDialogState('pick-existing'); }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>B) Use Existing Folder</div>
                      <div style={{ fontSize: 12, opacity: 0.7, fontWeight: 400, marginTop: 2 }}>
                        Browse your saved folders and launch one in MATLAB VNC
                      </div>
                    </div>
                  </Button>
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDialogState('idle')}>Back</Button>
                <Button onClick={closeDialog}>Cancel</Button>
              </DialogActions>
            </>
          )}

          {/* ── pick-existing: file-explorer style folder picker ── */}
          {dialogState === 'pick-existing' && (
            <>
              {/* Title bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 24px 10px',
                borderBottom: `1px solid ${theme.palette.divider}`, background: theme.palette.background.default }}>
                <FolderIcon style={{ color: theme.palette.primary.main, fontSize: 22 }} />
                <Typography variant="h6" style={{ flex: 1, fontSize: '1rem', fontWeight: 700 }}>
                  Open Folder — MATLAB VNC
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {loadingDisk ? 'Loading…' : `${diskFolders.length} folder${diskFolders.length !== 1 ? 's' : ''} found`}
                </Typography>
              </div>

              <DialogContent style={{ padding: '0', minHeight: 300 }}>
                {/* Column headers */}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px,1fr) 110px 160px 130px',
                  padding: '4px 16px', borderBottom: `1px solid ${theme.palette.divider}`, background: theme.palette.background.default }}>
                  <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.text.primary }}>Name</Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.text.primary }}>Status</Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.text.primary }}>Last Saved</Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.text.primary }}>Owner</Typography>
                </div>

                {loadingDisk ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
                    <CircularProgress size={32} />
                  </div>
                ) : diskFolders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 24px' }}>
                    <FolderIcon style={{ fontSize: 48, color: theme.palette.action.disabled }} />
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                      No saved folders found. Go back and create a new one.
                    </Typography>
                  </div>
                ) : (
                  <div style={{ overflowY: 'auto', maxHeight: 360 }}>
                    {/* Section divider for shared folders */}
                    {diskFolders.some((f: any) => !f.is_mine) && diskFolders.some((f: any) => f.is_mine) && (
                      null /* dividers injected inline below */
                    )}
                    {(() => {
                      const mine = diskFolders.filter((f: any) => f.is_mine);
                      const shared = diskFolders.filter((f: any) => !f.is_mine);
                      let rowIdx = 0;
                      const renderRow = (f: any) => {
                        const isSel = pickedFolder?.folder_name === f.folder_name && pickedFolder?.owner_username === f.owner_username;
                        const inUse = !!f.active_session_id;
                        const savedAt = f.last_saved_at
                          ? new Date(f.last_saved_at).toLocaleString()
                          : '—';
                        const idx = rowIdx++;
                        return (
                          <div key={`${f.owner_username}-${f.folder_name}`}
                            onClick={() => !inUse && setPickedFolder(isSel ? null : f)}
                            onDoubleClick={() => { if (!inUse) { setPickedFolder(f); if (f.is_mine) { doWorkspaceLaunch(f.folder_name); } else { doLaunchExistingById(f.id!, f.folder_name); } } }}
                            style={{
                              display: 'grid', gridTemplateColumns: 'minmax(160px,1fr) 110px 160px 130px',
                              alignItems: 'center', padding: '7px 16px',
                              cursor: inUse ? 'not-allowed' : 'pointer',
                              background: isSel ? theme.palette.action.selected : idx % 2 === 0 ? theme.palette.background.paper : theme.palette.background.default,
                              borderLeft: isSel ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                              opacity: inUse ? 0.65 : 1, userSelect: 'none' as const, overflow: 'hidden',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                              <FolderIcon fontSize="small"
                                style={{ color: isSel ? theme.palette.primary.main : f.last_saved_at ? '#f9a825' : theme.palette.text.disabled, flexShrink: 0 }} />
                              <span style={{ fontWeight: isSel ? 600 : 400, fontSize: 13,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                color: isSel ? theme.palette.primary.dark : 'inherit' }}>
                                {f.folder_name}
                              </span>
                            </div>
                            <div>
                              {inUse
                                ? <Chip label="In Use" size="small" style={{ background: theme.palette.success.main, color: theme.palette.success.contrastText, height: 18, fontSize: 10, fontWeight: 700 }} />
                                : f.is_public
                                ? <Chip label="Public" size="small" color="primary" style={{ height: 18, fontSize: 10 }} />
                                : <Chip label="Private" size="small" variant="outlined" style={{ height: 18, fontSize: 10 }} />
                              }
                            </div>
                            <Typography variant="caption" color="textSecondary" style={{ fontSize: 10 }}>
                              {savedAt}
                            </Typography>
                            <Typography variant="caption" style={{ fontSize: 10, color: f.is_mine ? theme.palette.success.main : theme.palette.primary.main }}>
                              {f.is_mine ? '👤 You' : `👥 ${f.owner_username}`}
                            </Typography>
                          </div>
                        );
                      };
                      return (
                        <>
                          {mine.length > 0 && (
                            <>
                              <div style={{ padding: '4px 16px', background: theme.palette.background.default, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.warning.main, fontSize: 10 }}>
                                  MY FOLDERS ({mine.length})
                                </Typography>
                              </div>
                              {mine.map(renderRow)}
                            </>
                          )}
                          {shared.length > 0 && (
                            <>
                              <div style={{ padding: '4px 16px', background: theme.palette.background.paper, borderBottom: `1px solid ${theme.palette.divider}`, borderTop: mine.length > 0 ? `2px solid ${theme.palette.divider}` : undefined }}>
                                <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.success.main, fontSize: 10 }}>
                                  SHARED BY OTHERS ({shared.length})
                                </Typography>
                              </div>
                              {shared.map(renderRow)}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </DialogContent>

              {/* Footer */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                borderTop: `1px solid ${theme.palette.divider}`, background: theme.palette.background.default }}>
                <div style={{ flex: 1, padding: '6px 10px', border: `1px solid ${theme.palette.divider}`, borderRadius: 3,
                  background: theme.palette.background.paper, fontSize: 13, color: pickedFolder ? theme.palette.text.primary : theme.palette.text.disabled,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                  {pickedFolder
                    ? `${pickedFolder.folder_name}${!pickedFolder.is_mine ? ` (by ${pickedFolder.owner_username})` : ''}`
                    : 'No folder selected'}
                </div>
                <Button size="small" onClick={() => setDialogState('choose-folder')} style={{ minWidth: 70 }}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" size="small"
                  disabled={!pickedFolder}
                  style={{ minWidth: 70 }}
                  onClick={() => { if (pickedFolder) { if (pickedFolder.is_mine) { doWorkspaceLaunch(pickedFolder.folder_name); } else { doLaunchExistingById(pickedFolder.id!, pickedFolder.folder_name); } } }}>
                  Open
                </Button>
              </div>
            </>
          )}

          {/* ── new-name: type folder name ── */}
          {dialogState === 'new-name' && (
            <>
              <DialogTitle id="matlab-dialog-title">Create New Folder</DialogTitle>
              <DialogContent>
                {errorMsg && (
                  <Typography color="error" variant="body2" style={{ marginBottom: 10 }}>{errorMsg}</Typography>
                )}
                <TextField
                  label="Folder name"
                  variant="outlined"
                  fullWidth
                  size="small"
                  autoFocus
                  value={newFolderName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFolderName(e.target.value)}
                  helperText="Letters, numbers, dots, underscores and hyphens only."
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' && newFolderName.trim()) doWorkspaceLaunch(newFolderName.trim());
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDialogState('choose-folder')}>Back</Button>
                <Button variant="contained" color="primary" disabled={!newFolderName.trim()}
                  onClick={() => doWorkspaceLaunch(newFolderName.trim())}>
                  Launch
                </Button>
              </DialogActions>
            </>
          )}

          {/* ── launching spinner ── */}
          {dialogState === 'launching' && (
            <>
              <DialogTitle id="matlab-dialog-title">Launching MATLAB…</DialogTitle>
              <DialogContent>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2">Starting MATLAB container…</Typography>
                </div>
              </DialogContent>
              <DialogActions>
                <Button disabled>Cancel</Button>
              </DialogActions>
            </>
          )}

          {/* ── busy: all licenses taken ── */}
          {dialogState === 'busy' && (
            <>
              <DialogTitle id="matlab-dialog-title">All Licenses Busy</DialogTitle>
              <DialogContent>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">
                    All {licenseStatus?.total ?? 4} licenses are in use. Checking every 15 s…
                  </Typography>
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeDialog}>Cancel</Button>
              </DialogActions>
            </>
          )}

        </Dialog>
      </Content>
    </Page>
  {/* Render floating session cards for each minimized session */}
    {sessions.length > 0 && ReactDOM.createPortal(
      <div style={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: '12px', pointerEvents: 'none', zIndex: 9998 }}>
        {sessions.map((s) => (
          <div key={s.sessionId} style={{ pointerEvents: 'auto' }}>
            <MatlabVncSessionCard sessionId={s.sessionId} />
          </div>
        ))}
      </div>,
      document.body
    )}
  </>
);
};

